<script setup lang="ts">
import type { Component } from "vue";
import ConditionValueBar from "./ConditionValueBar.vue";
import type { ValueRange } from "@/utils/conditionBar";
import type { TemperatureUnit } from "@/stores/settings.store";

withDefaults(
  defineProps<{
    label: string;
    detail?: string;
    icon: Component;
    color: string;
    value: string;
    valueUnit?: string;
    kind: "humidity" | "wind" | "pressure";
    current: number | null;
    observed?: ValueRange | null;
    forecast?: ValueRange | null;
    scale: ValueRange;
    barUnit: string;
    unitPreference: TemperatureUnit;
    wallMode?: boolean;
    fullWidth?: boolean;
  }>(),
  {
    detail: undefined,
    valueUnit: undefined,
    observed: undefined,
    forecast: undefined,
    wallMode: false,
    fullWidth: false,
  },
);
</script>

<template>
  <div
    class="grid items-center"
    :class="[
      fullWidth
        ? 'grid-cols-[22px_minmax(0,1fr)_auto]'
        : wallMode
          ? 'grid-cols-[22px_minmax(0,1fr)] compact-wall:grid-cols-[22px_minmax(0,1fr)_auto] [&_strong]:col-start-2 [&_strong]:text-left compact-wall:[&_strong]:col-start-3 compact-wall:[&_strong]:row-start-1 compact-wall:[&_strong]:text-right'
          : 'grid-cols-[22px_minmax(0,1fr)_auto]',
      wallMode
        ? 'gap-x-2 gap-y-[3px] short-wall:gap-y-px'
        : 'gap-x-2.5 gap-y-2',
      { 'col-span-full': fullWidth },
    ]"
    :style="{ '--metric-color': color }"
  >
    <component
      :is="icon"
      class="stroke-[1.65] text-[var(--metric-color)]"
      :size="22"
      aria-hidden="true"
    />
    <div
      class="leading-[1.4] text-text-muted"
      :class="wallMode ? 'text-xs tall-wall:text-[15px]' : 'text-[13px]'"
    >
      {{ label }}
      <span
        v-if="detail"
        class="mt-0.5 text-[11px]"
        :class="wallMode ? 'hidden' : 'block'"
      >
        {{ detail }}
      </span>
    </div>
    <strong
      class="leading-[1.2] font-[550] whitespace-nowrap text-[var(--metric-color)] tabular-nums [&_small]:text-xs [&_small]:leading-normal [&_small]:font-normal [&_small]:text-text-muted"
      :class="
        wallMode
          ? 'text-right text-base tall-wall:text-xl'
          : 'text-right text-[17px]'
      "
    >
      {{ value }} <small v-if="valueUnit">{{ valueUnit }}</small>
    </strong>
    <ConditionValueBar
      class="col-[2/-1]"
      :unit-preference="unitPreference"
      :label="label"
      :kind="kind"
      :current="current"
      :observed="observed"
      :forecast="forecast"
      :scale="scale"
      :unit="barUnit"
    />
  </div>
</template>
